import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, ArrowLeft, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketRecoveryPage = () => {
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return toast.error('Enter your phone number');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/events/recover-ticket`, { phone: phone.trim() });
      if (res.data.sent) {
        setDone(true);
      } else {
        toast.error("We couldn't find a ticket for that phone number. Double-check the number you used at checkout.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display font-black text-3xl uppercase tracking-tight mb-3">Check Your Texts</h1>
          <p className="text-muted-foreground mb-6">
            We've texted you the link to your ticket. Open the link from any phone or computer to watch the stream.
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            Don't see a text in 1–2 minutes? Make sure you entered the same phone number you used at Stripe checkout.
          </p>
          <Link to="/events">
            <Button size="lg" className="rounded-full px-8 font-bold uppercase tracking-wider">
              Back to Events
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <Link to="/events" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6" data-testid="recovery-back-link">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Events
        </Link>

        <div className="text-center mb-8">
          <MessageSquare className="w-12 h-12 text-primary mx-auto mb-3" />
          <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight">
            Recover Your Ticket
          </h1>
          <p className="text-muted-foreground mt-3">
            Bought a ticket but lost the link? Enter the phone number you used at checkout and we'll text it again.
          </p>
        </div>

        <form onSubmit={submit} className="bg-card border border-border rounded-2xl p-6 space-y-4">
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="pl-10"
                required
                data-testid="recovery-phone-input"
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Use the same number you entered at Stripe checkout.
            </p>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full font-bold uppercase tracking-wider"
            disabled={submitting}
            data-testid="recovery-submit"
          >
            {submitting ? 'Sending...' : 'Text Me My Link'}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Need help? Contact us through the chat bubble or reply to your purchase receipt email.
        </p>
      </div>
    </div>
  );
};

export default TicketRecoveryPage;
